<template>
  <v-avatar class="avatar-container" :size="size">
    <v-img
      v-if="avatarImageUrl"
      :src="avatarImageUrl"
      eager
      :cover="Boolean(user.avatar_url)"
      :alt="$translate('UserAvatar.alt', { name: user.display_name })"
      :title="user.display_name"
      class="rounded-circle"
    />
    <div
      v-else-if="showProcessingIndicator"
      class="avatar-loader-container d-flex flex-column justify-center align-center rounded-circle"
      :style="{ width: `${size}px`, height: `${size}px` }"
    >
      <loading-indicator :mode="processingIndicatorSize" />
      <span v-if="size > 200" class="mt-4 color-post-text-40 font-weight-bold">{{ $translate("common.processing") }}</span>
    </div>
    <slot v-else name="fallback-image">
      <i-custom-profile
        :class="{
          'color-post-background border-post-background background-post-detail': !wallBackground,
          'color-wall-background border-wall-background background-wall-detail': wallBackground
        }"
        class="fallback-image"
        :style="{ padding: `0 ${Math.floor(size * 0.18)}px` }"
      />
    </slot>

    <user-badge v-if="canShowBadge" class="avatar-badge" :style="{ 'max-width': `${size}px` }" :badge="user.badges[0]" />
    <div v-else-if="verified" class="verified d-flex justify-center align-center rounded-circle" :class="{ big: size >= 72, small: size < 48 }">
      <i-custom-tick class="color-white" />
    </div>
    <div class="overlay fill-width fill-height">
      <slot name="overlay" />
    </div>
  </v-avatar>
</template>

<script>
import * as images from "@/services/images.js"

import isEmpty from "lodash/isEmpty"

import { mapGetters } from "pinia"

import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"

import UserBadge from "@/components/users/UserBadge.vue"

export default {
  components: {
    UserBadge
  },
  props: {
    user: {
      type: Object,
      default: undefined
    },
    verified: {
      type: Boolean,
      default: false
    },
    size: {
      type: Number,
      default: 48
    },
    wallBackground: {
      type: Boolean,
      default: false
    },
    showBadge: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showProcessingIndicator: false
    }
  },
  computed: {
    ...mapGetters(useAppStateStore, ["localAvatarUrl"]),
    ...mapGetters(useUserSessionStore, { currentUser: "user" }),
    processingIndicatorSize() {
      if (this.size <= 30) {
        return "xs"
      }

      if (this.size <= 48) {
        return "sm"
      }

      return "md"
    },
    avatarImageUrl() {
      if (!this.user) {
        return null
      }

      if (this.currentUser?.id === this.user.id && this.localAvatarUrl) {
        return this.localAvatarUrl
      }

      if (this.showProcessingIndicator) {
        return
      }

      if (this.user.avatar_url) {
        return this.user.avatar_url
      }

      return this.user && images.getBestImageVersion(this.user.avatar, { type: "square", minHeight: Math.max(160, this.size) })?.url
    },
    canShowBadge() {
      return this.showBadge && this.user?.badges?.length
    }
  },
  watch: {
    user: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) {
          return
        }

        if (newVal.avatar?.versions && isEmpty(newVal.avatar.versions)) {
          this.$eventBus.$emit("listen-for-avatar-change")
          this.showProcessingIndicator = true
        } else {
          this.showProcessingIndicator = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.avatar-loader-container {
  background-color: var(--post-background);
  border: 3px solid var(--post-background);
}

.avatar-container {
  overflow: visible;
  .v-img {
    border-color: inherit !important;
  }

  svg {
    height: inherit;
    width: inherit;
    border-radius: inherit;
  }

  :deep(.svg-icon) {
    width: 100%;
    height: 100%;
  }

  .avatar-badge {
    position: absolute;
    bottom: -3px;
    right: 0;
    margin-right: 0;

    // Fix bug on Firefox: https://disciple.kanbanize.com/ctrl_board/52/cards/51426/details/
    :deep(.inline-svg-icon) {
      width: 12px;
    }
  }

  .color-white {
    color: var(--color-white) !important;
  }

  .verified {
    background-color: var(--color-success-green);
    position: absolute;
    bottom: 0;
    right: 0;
    width: 18px;
    height: 18px;

    &.big {
      width: 24px;
      height: 24px;
    }

    &.small {
      width: 12px;
      height: 12px;
    }
  }

  .overlay {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
